import { observable, action } from 'mobx'

import { StoreExt } from '@utils/reactExt'
import { extend } from 'lodash'

import { authStore, globalStore, userStore } from '@store/index'
import { IShopInfo, Schema$Product } from '@protocol/shop'
import { getErrorText } from '@game/errtext'
import { showToast, TOAST_TYPE } from '@utils/utils'
import { ERR } from 'adv-protocol/src/ErrorCodes'

export type ShopInfo = IShopInfo.Schema

export type Product = Schema$Product

export interface ProductTrackItem extends Product {
  img_full?: string
}

export class ShopStore extends StoreExt {
  imgPrefix = ''

  products: Product[]

  @observable
  tracks: ProductTrackItem[]

  constructor() {
    super()
    this.products = []
    this.tracks = []

    window.addEventListener('message', this.handleMessage)
  }

  handleMessage = async event => {
    const { data = {} } = event
    const { type, key, value = {} } = data
    if (type === 'nj') {
      if (key === 'njResponseGoogleInappBillingPurchased') {
        const { json, signature, price, currency } = value
        await this.inappPurchased(json, signature, price, currency)
      }
      if (key === 'njResponseGoogleInappBillingConsumed') {
        const { json, signature } = value
        await this.inappConsumed(json, signature)
      }
    }
  }

  inappPurchased = async (json: string, signature: string, price: string, currency: string) => {
    try {
      globalStore.pushDialogWait({ title: '알림', text: ['아이템 구입중입니다...'], className: 'small-size', })

      const { user, errcode } = await this.api.shop.inappPurchased({
        publish: process.env.APP_ENV,
        purchaseOriginalJson: json,
        purchaseSignature: signature,
        inappPrice: price,
        inappCurrencyCode: currency,
      })

      // // eslint-disable-next-line dot-notation
      // consume이 끝난 상태이기때문에 의미없는 로직
      // const android = window.android
      // if (android) {
      //   android.jnRequestGoogleInappBillingConsume(developerPayload)
      // }

      globalStore.closeDialog()

      if (errcode) {
        globalStore.pushDialogOk({
          title: '알림',
          text: ['서버 연결이 원활하지 않습니다.', '새로고침해 주세요.', `${errcode}`],
          className: 'small-size'
        })
      } else {
        globalStore.pushDialogOk({ title: '알림', text: ['구매를 완료했습니다.'], className: 'small-size' })
        authStore.updateUserAssets(user)
      }
    } catch (err) {
      globalStore.closeDialog()
      console.error(err)
      showToast(TOAST_TYPE.INFO, '구매 실패! 잠시후 다시 시도해 주세요.')
    }
  }

  inappConsumed = async (json: string, signature: string) => {
    //
  }

  @action
  fetch = async (params: IShopInfo.Params = {}) => {
    try {
      const { imgPrefix, items } = await this.api.shop.getShopInfo(params)
      this.imgPrefix = imgPrefix || ''
      this.products = items
      const tracks = []
      for (const el of this.products) {
        const track: ProductTrackItem = extend({}, el)
        if (el.img) {
          track.img_full = imgPrefix + el.img
        }

        tracks.push(track)
      }
      this.setProductList(tracks)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  buy = async (id: number, name: string, count: number, price: number, period: number) => {
    try {
      globalStore.closeDialog()
      globalStore.pushDialogWait({ title: '알림', text: ['아이템 구입중입니다...'], className: 'small-size' })

      const { errcode, user } = await this.api.shop.buyProduct({ id, count, price, period })
      // set something to dispatch result to ui
      globalStore.closeDialog()
      if (errcode) {
        if (errcode === ERR.PRODUCT_JOB_LIMITED) {
          const userInfo = authStore.userInfo
          if (userInfo.job === 1) {
            showToast(TOAST_TYPE.INFO, '클럽 전용 아이템입니다. 베터는 구매할 수 없습니다.')
          } else {
            showToast(TOAST_TYPE.INFO, '베터 전용 아이템입니다. 클럽마스터는 구매할 수 없습니다')
          }
          return false
        }
        if (errcode === ERR.PRODUCT_INSUFFICIENT_HEART) {
          showToast(TOAST_TYPE.INFO, '하트가 부족합니다.')
          return false
        }

        const errText = getErrorText(errcode) || '서버 연결이 원활하지 않습니다. 새로고침해 주세요.'
        showToast(TOAST_TYPE.INFO, `${errText}`)
      } else {
        showToast(TOAST_TYPE.INFO, `${name} 구매를 완료했습니다.`)
        await authStore.refreshSimpleUserInfo(true)
        authStore.updateUserAssets(user)

        if (id === 23) {
          const result = await userStore.getOwnedFreeLicense()
          if (result) {
            if (result.items?.length > 0) {
              globalStore.pushDialogOk({ text: [ '클럽 개설은 PC버전에서만 가능합니다.', '1. https://home.adventurer.co.kr', '2. 자료실에서 게임 다운로드', '3. 로그인 후 GAME START 버튼 클릭', '4. 게임 접속 후 마이룸으로 이동 후 클럽 개설' ], className: 'align-left-dialog small-size'})
            }
          }
        }
        return true
      }
    } catch (err) {
      globalStore.closeDialog()
      console.error(err)
      showToast(TOAST_TYPE.INFO, '구매 실패! 잠시후 다시 시도해 주세요.')
    }

    return false
  }

  @action
  setProductList = (tracks: ProductTrackItem[]): ProductTrackItem[] => {
    this.tracks = tracks
    return tracks
  }
}

export default new ShopStore()
